import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, BookOpen, Briefcase, Check, Globe, Sparkles, Store } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeploymentStep } from './DeploymentStep';
import { DiscordAutomaticIdle } from './DiscordAutomaticIdle';
import { ExternalAppsStep } from './ExternalAppsStep';
import { OnboardingProgressDots } from './OnboardingProgressDots';
import { DESCRIPTION_COPY, OnboardingSetupForm } from './OnboardingSetupForm';
import { PricingStepContainer } from './PricingStepContainer';
import { SaasRemedyHint } from './SaasRemedyHint';
import {
  AutoOnboardingErrorCategory,
  AutoOnboardingSourceType,
  AutoOnboardingStep,
  extractUrlHost,
  LowQualityReason,
  useAutomaticOnboarding,
} from './useAutomaticOnboarding';
import { useEnterToAdvance } from './useEnterToAdvance';
import { GettingStartedProgressDto, OnboardingBrand } from './useGettingStartedOnboarding';
import type { DiscordGuildSummary } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import LANGUAGES from '@/Domain/components/Settings/ai/LANGUAGES';
import LanguageMultiSelect from '@/Domain/components/Settings/ai/LanguageMultiSelect';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldLabel } from '@/Domain/components/Shared/Field';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { classifySaasHost } from '@/Domain/helper/saasHostRouter';
import { parseHost } from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { cn } from '@/Domain/utils/cn';
import { IDENTITY_DEFAULTS, PERSONALITY_OPTIONS } from '@/UI/Identity/Identity.constants';
import { forgetSiteMap } from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

/**
 * Automatic onboarding flow: user provides a URL and the backend customizes
 * their AI Agent in real-time, pushing incremental progress via websockets.
 *
 * State machine:
 *   idle       → URL input form
 *   processing → visual progress card (updates in-place, no jumping)
 *   completed  → embedded widget preview
 *   error      → error message with retry
 */

type SummaryItem = {
  icon: string;
  label: string;
  // Raw enum value plus which setting it is, so the summary can name it with the
  // exact strings the Identity page shows instead of a second English-only list
  // that drifts. `label` is the pre-field fallback.
  field?: 'profession' | 'personality' | 'languages';
  value?: string | number;
};

type LanguageCode = (typeof LANGUAGES)[number];

const LANGUAGE_BY_FOLDED_CODE = new Map(
  LANGUAGES.map((code) => [code.toLowerCase(), code] as const),
);

const parseLanguageCodes = (value: string | number | undefined): LanguageCode[] => {
  if (value === undefined) return [];
  const rawCodes = String(value)
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
  const codes: LanguageCode[] = [];
  for (const rawCode of rawCodes) {
    const code = LANGUAGE_BY_FOLDED_CODE.get(rawCode.toLowerCase());
    if (code === undefined) return [];
    if (!codes.includes(code)) codes.push(code);
  }
  return codes;
};

/** Names a generated setting the way the Identity page does, localized. */
const useSummaryItemLabel = () => {
  const { t } = useTranslation(['getting_started', 'settings', 'common']);
  return (item: SummaryItem): string => {
    if (item.field === undefined || item.value === undefined) return item.label;
    if (item.field === 'languages') return item.label;
    const defaultTag = ` (${t('common:default', { defaultValue: 'Default' })})`;
    if (item.field === 'profession') {
      const name = t(`settings:profession.options.${item.value}`, {
        defaultValue: String(item.value),
      });
      const suffix = item.value === IDENTITY_DEFAULTS.profession ? defaultTag : '';
      return `${t('settings:profession.label')}: ${name}${suffix}`;
    }
    const key = PERSONALITY_KEY_BY_LEVEL[Number(item.value)];
    if (!key) return item.label;
    const name = t(`common:personalities.${key}`, { defaultValue: key });
    const suffix = Number(item.value) === IDENTITY_DEFAULTS.personality ? defaultTag : '';
    return `${t('settings:personality.label')}: ${name}${suffix}`;
  };
};

// Derived from the Identity domain's own option list rather than a second copy:
// the whole point of this path is that the summary names settings exactly as
// Identity does, so a duplicated table would reintroduce the drift it fixes.
const PERSONALITY_KEY_BY_LEVEL: Record<number, string> = Object.fromEntries(
  PERSONALITY_OPTIONS.map(({ value, key }) => [value, key]),
);

/**
 * The starters the generator wrote, shown inside the mini widget mock the way the
 * real widget stacks them. Both source types emit them and neither screen showed
 * them, even though the description flow advertises them twice while it runs.
 *
 * Non-interactive on purpose: a `ul` of `li`, no click handler, no hover state.
 * This is a display of what was built, not a chat the user can talk to, and the
 * card cannot deliver on a button that looks pressable.
 */
const ConversationStarterPills = ({ starters }: { starters: string[] }) => {
  const { t } = useTranslation('widget');
  return (
    <ul
      // Without this a screen reader hears three unexplained list items after
      // the greeting.
      aria-label={t('conversation_starters.label')}
      className="flex flex-col items-start gap-1.5 px-3 pb-2.5"
      data-testid="gs-automatic-conversation-starters"
    >
      {starters.map((starter, index) => (
        <li
          key={`${index}-${starter}`}
          // `bg-muted`, not `bg-card`: the card body is already `bg-card`, so a
          // card-coloured pill renders as a hollow outline. `bg-muted` is the
          // same fill the mock's own header bar uses.
          className="max-w-[80%] break-words rounded-full bg-muted px-3 py-1.5 text-left text-sm text-foreground"
          data-testid={`gs-automatic-conversation-starter-${index}`}
        >
          {starter}
        </li>
      ))}
    </ul>
  );
};

/** Cycles through action verbs with a fade transition. */
const RotatingVerb = () => {
  const { t } = useTranslation('getting_started');
  // The 20-item verb list lives in `getting_started.automatic_flow.loading_verbs`
  // per locale; `returnObjects` returns the JSON array as-is. `useMemo` keyed on
  // `t` keeps the array identity stable across renders so the interval effect
  // below doesn't thrash. Fallback to an empty array if the translation ever
  // drifts into a non-array (defensive; `t` types it as `unknown`).
  const verbs = useMemo(() => {
    const raw = t('automatic_flow.loading_verbs', { returnObjects: true });
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [t]);
  const [index, setIndex] = useState(() =>
    verbs.length > 0 ? Math.floor(Math.random() * verbs.length) : 0,
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (verbs.length === 0) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % verbs.length);
        setVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [verbs.length]);

  if (verbs.length === 0) return null;

  return (
    <p
      className={cn(
        'text-sm loading-text [animation-duration:6s] transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      {verbs[index]}…
    </p>
  );
};

// Idle → processing hand-off. `submitDescription`/`submitUrl` flip the state in
// the same tick as the dispatch, so without this the form is gone before the
// button's spinner paints and the submit reads as an unexplained jump.
const STATE_SWAP_INITIAL = { opacity: 0, y: 8 } as const;
const STATE_SWAP_EXIT = { opacity: 0, y: -8 } as const;
const STATE_SWAP_ENTER_TRANSITION = { duration: 0.28, ease: [0.22, 1, 0.36, 1] } as const;
const STATE_SWAP_EXIT_TRANSITION = { duration: 0.22, ease: [0.4, 0, 1, 1] } as const;

// Language names are resolved on the backend using quickchat.translation.languages.language_index

/** Extract accumulated visual state from the step stream. */
function useProgressSummary(steps: AutoOnboardingStep[]) {
  return useMemo(() => {
    let phase: 'analyzing' | 'scraping' | 'configuring' | 'done' = 'analyzing';
    let pageTitle: string | null = null;
    let avatarUrl: string | null = null;
    let agentName: string | null = null;
    let welcomeMessage: string | null = null;
    let conversationStarters: string[] = [];
    let isFinished = false;
    let summaryItems: SummaryItem[] = [];

    for (const step of steps) {
      if (step.step === 'page_scraped') {
        phase = 'scraping';
        pageTitle = step.data?.title || null;
      }
      if (step.step === 'avatar_resolved') {
        avatarUrl = step.data?.avatar_url || null;
      }
      if (step.step === 'settings_generating') {
        phase = 'configuring';
      }
      if (step.step === 'setting_applied') {
        if (step.data?.setting === 'ai_agent_name') agentName = step.data.value ?? null;
        if (step.data?.setting === 'welcome_message') welcomeMessage = step.data.value ?? null;
        if (step.data?.setting === 'avatar') avatarUrl = step.data.value ?? avatarUrl;
        if (step.data?.setting === 'conversation_starters') {
          // Same `unknown` hop as `summary` below: `data` is typed
          // `Record<string, string>` but this value is a list on the wire. The
          // runtime guard is the part that matters — a cast is a claim, and this
          // one crosses a websocket.
          const raw = step.data.value as unknown as string[] | undefined;
          if (Array.isArray(raw)) {
            conversationStarters = raw.filter(
              (starter) => typeof starter === 'string' && starter.trim().length > 0,
            );
          }
        }
      }
      if (step.step === 'completed') {
        phase = 'done';
        isFinished = true;
        summaryItems = (step.data?.summary as unknown as SummaryItem[]) || [];
      }
    }

    return {
      phase,
      pageTitle,
      avatarUrl,
      agentName,
      welcomeMessage,
      conversationStarters,
      isFinished,
      summaryItems,
    };
  }, [steps]);
}

// Phase → translation-key suffix.
const PHASE_LABEL_KEY: Record<string, string> = {
  analyzing: 'automatic_flow.phase.analyzing',
  scraping: 'automatic_flow.phase.scraping',
  configuring: 'automatic_flow.phase.configuring',
  done: 'automatic_flow.phase.done',
};

// A description build is one long silent LLM call — unlike the URL flow there is
// no scrape or article to stream, so nothing moves for ~20s. These name the
// fields the model is genuinely producing in that call, so the wait reads as
// work rather than a hang.
const DESCRIPTION_WAIT_INTERVAL_MS = 2800;

const DescriptionWaitHint = ({ brand }: { brand: OnboardingBrand }) => {
  const { t } = useTranslation('getting_started');
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const waitKeys = DESCRIPTION_COPY[brand].wait;

  useEffect(() => {
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % waitKeys.length),
      DESCRIPTION_WAIT_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [waitKeys]);

  return (
    <span className="flex items-center gap-2" data-testid="gs-description-wait">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={index}
          initial={reduceMotion ? undefined : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
          transition={{ duration: 0.25 }}
        >
          {t(waitKeys[index])}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const SUMMARY_LANGUAGE_PICKER_ID = 'gs-summary-languages';

const sameLanguageCodes = (left: LanguageCode[], right: LanguageCode[]): boolean =>
  left.length === right.length && left.every((code, index) => code === right[index]);

const SummaryLanguagePicker = ({ initialCodes }: { initialCodes: LanguageCode[] }) => {
  const { t } = useTranslation(['getting_started', 'settings']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const { getConfig, isFetched } = useSubscription();
  const [codes, setCodes] = useState<LanguageCode[]>(initialCodes);
  const confirmedCodesRef = useRef(initialCodes);
  const desiredCodesRef = useRef(initialCodes);
  const languageLimit = getConfig('languages') as number | undefined;

  const updateCachedLanguages = (languages: LanguageCode[]) => {
    queryClient.setQueryData(['settings'], (old: Record<string, unknown> | undefined) =>
      old ? { ...old, languages } : old,
    );
  };

  const mutation = useMutation<
    { languages?: LanguageCode[] },
    unknown,
    { languages: LanguageCode[] }
  >({
    mutationKey: ['settings'],
    scope: { id: 'settings-languages' },
    onSuccess: (data, variables) => {
      const serverCodes = data.languages ?? variables.languages;
      confirmedCodesRef.current = serverCodes;
      if (!sameLanguageCodes(desiredCodesRef.current, variables.languages)) return;
      desiredCodesRef.current = serverCodes;
      setCodes(serverCodes);
      updateCachedLanguages(serverCodes);
    },
    onError: (_error, variables) => {
      if (!sameLanguageCodes(desiredCodesRef.current, variables.languages)) return;
      const confirmedCodes = confirmedCodesRef.current;
      desiredCodesRef.current = confirmedCodes;
      setCodes(confirmedCodes);
      updateCachedLanguages(confirmedCodes);
    },
  });

  const handleChange = (next: LanguageCode[]) => {
    if (next.length === 0) return;
    desiredCodesRef.current = next;
    setCodes(next);
    updateCachedLanguages(next);
    capture(PostHogEventEnum.GettingStartedAutomaticLanguageChanged, {
      language_count: next.length,
    });
    mutation.mutate({ languages: next });
  };

  return (
    <span className="flex min-w-0 items-center gap-2">
      {/* Plain weight and a shrink-proof label so the row reads like the static
          Profession/Personality rows above it rather than a form field. */}
      <FieldLabel htmlFor={SUMMARY_LANGUAGE_PICKER_ID} className="shrink-0 font-normal">
        {t('settings:languages.title', { defaultValue: 'Languages' })}:
      </FieldLabel>
      <LanguageMultiSelect
        id={SUMMARY_LANGUAGE_PICKER_ID}
        value={codes}
        onChange={handleChange}
        limit={isFetched ? languageLimit : undefined}
        // Four things made this read as a form field dropped into a list of
        // facts, and the label alone could not reach any of them:
        //   h-auto py-0   - a 34px control against 20px text rows, which also
        //                   dropped its icon ~7px below the other two
        //   -mx-1 px-1    - nets to zero, so the value starts at the same x as
        //                   "Support Agent" while the hover target keeps padding
        //   w-auto        - shrink-wrap instead of a 320px box holding "English"
        //   border-0 bg-transparent - the control was bg-card on a bg-card
        //                   container, so all that rendered was a hollow outline
        // hover:bg-accent and data-[state=open]:bg-accent survive: that highlight
        // is what tells the user this particular fact is editable.
        triggerClassName={cn(
          'h-auto gap-1 border-0 bg-transparent p-0 font-normal text-foreground',
          // A fixed width so the row never reflows as languages are added or
          // removed. `justify-start` keeps the chevron against the text instead of
          // parked at the far edge, and with no border or fill the reserved space
          // past it is invisible: nothing follows this control in the row.
          'w-52 shrink-0 justify-start',
          // The bordered variant's grey block is too loud for a line of prose. It
          // has to be cancelled at its own specificity, arbitrary variant and all,
          // or tailwind-merge keeps both. A dotted underline says "editable"
          // without painting a box mid-sentence.
          '[&:not(:disabled)]:hover:bg-transparent data-[state=open]:border-0',
          'data-[state=open]:bg-transparent data-[state=open]:ring-0',
          'decoration-muted-foreground decoration-dotted underline-offset-4',
          'hover:underline data-[state=open]:underline',
          '[&>svg]:size-3.5 [&>svg]:text-muted-foreground',
        )}
        data-testid={SUMMARY_LANGUAGE_PICKER_ID}
      />
    </span>
  );
};

const ProgressView = ({
  steps,
  submittedUrl,
  warningReason,
  sourceType,
  brand,
  footer,
}: {
  steps: AutoOnboardingStep[];
  submittedUrl: string | null;
  warningReason: LowQualityReason | null;
  sourceType: AutoOnboardingSourceType | null;
  brand: OnboardingBrand;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation('getting_started');
  const {
    phase,
    pageTitle,
    avatarUrl,
    agentName,
    welcomeMessage,
    conversationStarters,
    isFinished,
    summaryItems,
  } = useProgressSummary(steps);
  const summaryItemLabel = useSummaryItemLabel();

  const phaseKey = PHASE_LABEL_KEY[phase];
  const phaseLabel =
    sourceType === 'description' && !isFinished
      ? t(DESCRIPTION_COPY[brand].working)
      : phaseKey
        ? t(phaseKey)
        : t('automatic_flow.working');

  // Extract domain from submitted URL for immediate display
  const domain = useMemo(() => {
    if (!submittedUrl) return null;
    return parseHost(submittedUrl) ?? submittedUrl;
  }, [submittedUrl]);

  // Show the preview card as soon as we have the avatar (prefetched in ~0.3s)
  // or once settings start arriving
  const showPreviewCard = avatarUrl || agentName || domain;

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-2xl p-6 flex flex-col gap-6',
        footer && 'min-h-[500px]',
      )}
    >
      {/* Header — always stable, updates in-place. On completion the phase
          label swaps to the "ready" copy; when the homepage was low-quality
          it instead reframes to a calm "preview / start over" so the header
          doesn't celebrate a result the warning below is cautioning against. */}
      <div>
        <p className="text-xl font-semibold text-foreground">
          {isFinished && warningReason ? t('automatic_flow.low_quality.summary_title') : phaseLabel}
        </p>
        <p className="text-sm font-medium text-muted-foreground mt-1">
          {!isFinished ? (
            sourceType === 'description' ? (
              <DescriptionWaitHint brand={brand} />
            ) : (
              t('automatic_flow.timing_hint')
            )
          ) : warningReason ? (
            t('automatic_flow.low_quality.summary_hint')
          ) : (
            t('automatic_flow.ready_hint')
          )}
        </p>
      </div>

      {warningReason && (
        <Alert variant="warning" data-testid="gs-automatic-low-quality">
          <AlertTitle>{t('automatic_flow.low_quality.title')}</AlertTitle>
          <AlertDescription>
            {t(LOW_QUALITY_COPY_KEY[warningReason], { domain: domain ?? submittedUrl ?? '' })}
          </AlertDescription>
        </Alert>
      )}

      {/* Page title — show domain immediately, upgrade to full title when scraped */}
      {(pageTitle || domain || submittedUrl) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground transition-opacity duration-300">
          <Globe className="size-3.5 text-muted-foreground flex-shrink-0" />
          <span className="truncate" data-testid="gs-automatic-progress-domain">
            {pageTitle || domain || submittedUrl}
          </span>
        </div>
      )}

      {/* Agent preview card — shows domain+avatar immediately, fills in as settings arrive */}
      {showPreviewCard && (
        <div className="border border-border rounded-xl overflow-hidden transition-all duration-300">
          {/* Mini header bar */}
          <div className="h-10 flex items-center gap-2.5 px-3 bg-muted transition-colors duration-500">
            {avatarUrl && (
              <img
                // Key on the URL so React re-mounts the element instead of
                // reusing the previous DOM node when the backend upgrades
                // the avatar mid-run. Without the key, an `onError`-inlined
                // `display: none` sticks to the element and hides a later,
                // valid URL too.
                key={avatarUrl}
                src={avatarUrl}
                alt=""
                className="size-6 rounded-full object-contain bg-background/90 transition-opacity duration-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="text-sm font-medium truncate text-foreground transition-colors duration-300">
              {agentName || domain}
            </span>
          </div>

          {/* Welcome message area */}
          <div className="px-3 py-2.5 min-h-[2.5rem] flex items-center">
            {welcomeMessage ? (
              <p className="text-sm text-foreground transition-opacity duration-300">
                {welcomeMessage}
              </p>
            ) : !isFinished ? (
              <RotatingVerb />
            ) : null}
          </div>

          {/* Deliberately not gated on `isFinished`: the backend emits the
              starters after the welcome message, so the card visibly grows while
              the work is still happening, which is the point of showing them. */}
          {conversationStarters.length > 0 && (
            <ConversationStarterPills starters={conversationStarters} />
          )}
        </div>
      )}

      {/* Summary of what was configured — shown on completion */}
      {isFinished && summaryItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {summaryItems.map((item, i) => {
            const IconComponent =
              item.icon === 'briefcase'
                ? Briefcase
                : item.icon === 'sparkles'
                  ? Sparkles
                  : item.icon === 'globe'
                    ? Globe
                    : item.icon === 'store'
                      ? Store
                      : item.icon === 'book'
                        ? BookOpen
                        : Check;
            const isArticleItem = item.icon === 'book';
            const itemLabel = summaryItemLabel(item);
            const languageCodes =
              item.field === 'languages' ? parseLanguageCodes(item.value) : null;
            return (
              <div
                key={i}
                // Every row is now a plain text line, so none of them needs the
                // negative margin the taller language control used to require.
                className="flex items-center gap-2 text-sm text-foreground"
                data-testid={`gs-automatic-summary-row-${item.field ?? item.icon}`}
              >
                <IconComponent className="size-4 text-green-500 flex-shrink-0" />
                {languageCodes?.length ? (
                  <SummaryLanguagePicker initialCodes={languageCodes} />
                ) : (
                  <span>
                    {isArticleItem ? t('automatic_flow.kb_background_scrape') : itemLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isFinished && sourceType === 'description' && <DescriptionSetupHint brand={brand} />}

      {footer && <div className="mt-auto flex items-center justify-end gap-3">{footer}</div>}
    </div>
  );
};

const DescriptionSetupHint = ({ brand }: { brand: OnboardingBrand }) => {
  const { t } = useTranslation('getting_started');
  const copy = DESCRIPTION_COPY[brand];
  return (
    <Alert variant="info" data-testid="gs-description-next-steps">
      {/* Alert positions its icon absolutely, sets no size, and forces
          text-foreground over the variant's colour. So an unsized lucide icon
          renders at 24px against a 14px title, sitting low and in black inside a
          blue alert. Both overrides are the house pattern for a coloured Alert. */}
      <BookOpen className="size-4 !text-info" />
      <AlertTitle>{t(copy.nextStepsTitle)}</AlertTitle>
      <AlertDescription>{t(copy.nextStepsDescription)}</AlertDescription>
    </Alert>
  );
};

// Post-processing stepper that runs after the backend is done. The automatic
// flow walks the user through the same External Apps, Deployment, and Pricing
// containers as the manual checklist, reusing the components one-for-one.
// Pricing is terminal — clicking Skip-for-now (or picking a tier and returning
// from Stripe) is what finishes the automatic flow.
type PostStep = 'summary' | 'external_apps' | 'deployment' | 'pricing';

const POST_STEP_ORDER: PostStep[] = ['summary', 'external_apps', 'deployment', 'pricing'];

// The progress-row flag each step owns, so skipping a step can still record it
// as done. `summary` has no flag (its position is derived from the absence of
// the others) and `pricing` is written by the terminal Finish handler.
const POST_STEP_FLAG: Partial<Record<PostStep, keyof GettingStartedProgressDto>> = {
  external_apps: 'external_apps_step_completed',
  deployment: 'deployment_step_completed',
};

const PostStepProgressDots = ({
  currentStep,
  order,
}: {
  currentStep: PostStep;
  order: PostStep[];
}) => (
  <OnboardingProgressDots
    totalSteps={order.length}
    currentIndex={order.indexOf(currentStep)}
    testIdPrefix="gs-automatic-progress-dot"
  />
);

// Translation-key suffix per user-facing category. Keeps the mapping in one
// place so adding a new category means adding one line here + one entry in
// each locale file (keys under `automatic_flow.error.<suffix>`).
const CATEGORY_I18N_KEY: Record<AutoOnboardingErrorCategory, string> = {
  scrape_failed: 'automatic_flow.error.scrape_failed',
  access_blocked: 'automatic_flow.error.access_blocked',
  timeout: 'automatic_flow.error.timeout',
  dns_failure: 'automatic_flow.error.dns_failure',
  website_not_supported: 'automatic_flow.error.website_not_supported',
  bad_request: 'automatic_flow.error.bad_request',
  already_running: 'automatic_flow.error.already_running',
  server_error: 'automatic_flow.error.server_error',
  network: 'automatic_flow.error.network',
  unknown: 'automatic_flow.error.unknown',
};

// Advisory low-quality-source copy. `blocked` = the homepage looked like a
// maintenance/login/error page; `thin` = it read as empty/near-empty;
// `auth_wall` = a sign-in page, where the fix is a different URL rather than a retry.
const LOW_QUALITY_COPY_KEY: Record<LowQualityReason, string> = {
  http_error: 'automatic_flow.low_quality.blocked',
  error_page: 'automatic_flow.low_quality.blocked',
  soft_404: 'automatic_flow.low_quality.thin',
  empty_content: 'automatic_flow.low_quality.thin',
  thin_content: 'automatic_flow.low_quality.thin',
  off_domain_redirect: 'automatic_flow.low_quality.wrong_site',
  auth_wall: 'automatic_flow.low_quality.auth_wall',
};

// Categories that mean "this host can never be scraped", so a concrete way in beats asking for a
// different URL. Anything else keeps its existing copy and CTAs.
const UNSCRAPEABLE_HOST_CATEGORIES: AutoOnboardingErrorCategory[] = [
  'access_blocked',
  'website_not_supported',
];

const ErrorView = ({
  category,
  urlHost,
  descriptionFlow = false,
  brand,
  onRetry,
  onTryDifferentWebsite,
  onSwitchToManual,
}: {
  category: AutoOnboardingErrorCategory;
  urlHost?: string;
  descriptionFlow?: boolean;
  brand: OnboardingBrand;
  onRetry: () => void;
  onTryDifferentWebsite?: () => void;
  onSwitchToManual?: () => void;
}) => {
  const { t } = useTranslation('getting_started');
  const saasRemedy =
    !descriptionFlow && UNSCRAPEABLE_HOST_CATEGORIES.includes(category)
      ? classifySaasHost(urlHost)
      : undefined;
  // Retrying the same bot-walled URL is futile, so lead with manual setup.
  const leadWithManual = category === 'access_blocked' && Boolean(onSwitchToManual);
  return (
    <div className="bg-card border border-destructive/20 rounded-2xl p-6 flex flex-col items-center text-center gap-4">
      <div className="size-12 rounded-xl flex items-center justify-center bg-destructive/10">
        <AlertCircle className="size-6 text-destructive" />
      </div>
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold text-foreground">{t('automatic_flow.error_title')}</h2>
        <p className="text-sm text-muted-foreground">
          {descriptionFlow
            ? t(DESCRIPTION_COPY[brand].generationError)
            : t(CATEGORY_I18N_KEY[category], { host: urlHost ?? '' })}
        </p>
      </div>
      {saasRemedy && <SaasRemedyHint remedy={saasRemedy} urlHost={urlHost} source="scrape_error" />}
      <div className="flex flex-col sm:flex-row gap-2 items-center">
        {leadWithManual ? (
          <>
            <Button
              variant="dark"
              size="md"
              onClick={onSwitchToManual}
              data-testid="gs-automatic-switch-to-manual"
            >
              {t('automatic_flow.error_setup_manually')}
            </Button>
            <Button
              variant="link"
              size="md"
              onClick={onTryDifferentWebsite ?? onRetry}
              data-testid="gs-automatic-retry"
            >
              {t('automatic_flow.error_try_different')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="dark" size="md" onClick={onRetry} data-testid="gs-automatic-retry">
              {t('automatic_flow.error_retry')}
            </Button>
            {onSwitchToManual && (
              <Button
                variant="link"
                size="md"
                onClick={onSwitchToManual}
                data-testid="gs-automatic-switch-to-manual"
              >
                {t('automatic_flow.error_switch_to_manual')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface AutomaticOnboardingProps {
  /**
   * Fired once when the backend finishes customizing the agent (state hits
   * `completed`). The parent uses this to flip its own `isOnboardingComplete`
   * flag and swap the page into the widget view — this component no longer
   * mounts the widget itself.
   */
  onComplete?: () => void;
  /** Tertiary manual-setup action below the description form and in URL validation notices. */
  onSkipToManual?: () => void;
  /**
   * Offered as a secondary action on the error view when a scrape fails —
   * lets the user abandon the URL-driven flow and fall back to the manual
   * checklist without losing the failure context.
   */
  onSwitchToManual?: () => void;
  /**
   * Page-level URL draft state. Lifted out of this component so the typed
   * URL survives motion's `key={mode}` remount when the user toggles
   * between automatic and manual mode (the previous local-state version
   * cleared the field on every toggle, which forced users to re-type their
   * URL when they came back from the manual checklist).
   *
   * Optional for compatibility with call sites that don't yet thread the
   * state through; when omitted the component falls back to local state.
   */
  urlDraft?: string;
  onUrlDraftChange?: (next: string) => void;
  // Lifted for the same reason as `urlDraft`: switching to manual setup and back
  // remounts this component, and a local draft would silently discard what the
  // user had already typed.
  descriptionDraft?: string;
  onDescriptionDraftChange?: (next: string) => void;
  /** Server-derived onboarding brand; 'discord' renders the Discord-branded idle screen. */
  brand?: OnboardingBrand;
}

interface PostStepCardProps {
  title: string;
  description: string;
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  isAdvancing?: boolean;
  children: ReactNode;
}

const PostStepCard = ({
  title,
  description,
  onBack,
  onNext,
  nextLabel,
  isAdvancing = false,
  children,
}: PostStepCardProps) => {
  const { t } = useTranslation('getting_started');
  return (
    <div className="flex flex-col gap-6 bg-card border border-border rounded-xl p-6 min-h-[500px]">
      <div>
        <p className="text-xl font-semibold text-foreground">{title}</p>
        <p className="text-sm font-medium text-muted-foreground mt-1">{description}</p>
      </div>

      <div className="flex-1">{children}</div>

      <div className="flex items-center justify-end gap-3">
        <Button
          size="sm"
          variant="link"
          onClick={onBack}
          disabled={isAdvancing}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-automatic-poststep-back"
        >
          {t('back')}
        </Button>
        <Button
          size="sm"
          onClick={onNext}
          loading={isAdvancing}
          className="min-w-16"
          data-testid="gs-automatic-poststep-next"
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
};

function getDiscordGuildConnectionTimestamp(guild: DiscordGuildSummary): number {
  const timestamp = Date.parse(guild.connected_at ?? guild.added_at ?? '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export const AutomaticOnboarding = ({
  onComplete,
  onSkipToManual,
  onSwitchToManual,
  urlDraft,
  onUrlDraftChange,
  descriptionDraft,
  onDescriptionDraftChange,
  brand = 'default',
}: AutomaticOnboardingProps) => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const {
    state,
    steps,
    errorCategory,
    warningReason,
    urlType,
    submittedUrl,
    submitUrl,
    submitDescription,
    isSubmitting,
    reset,
  } = useAutomaticOnboarding();
  // Local fallback for the URL draft when the parent hasn't lifted state.
  // Once GettingStartedPage is fully migrated this can become required.
  const [localUrlDraft, setLocalUrlDraft] = useState('');
  const effectiveUrlDraft = urlDraft ?? localUrlDraft;
  const effectiveSetUrlDraft = onUrlDraftChange ?? setLocalUrlDraft;
  const [localDescriptionDraft, setLocalDescriptionDraft] = useState('');
  const effectiveDescriptionDraft = descriptionDraft ?? localDescriptionDraft;
  const effectiveSetDescriptionDraft = onDescriptionDraftChange ?? setLocalDescriptionDraft;
  // Post-processing stepper state. Only consulted while `state === 'completed'`;
  // reset to `summary` whenever the backend goes back to a non-completed state
  // (e.g. error retry) so re-entering completion lands on the summary again.
  //
  // On fresh mount we derive the resume position from the cached progress row:
  // `external_apps_step_completed=true` means the user already clicked Continue
  // past the external-apps step, so they were on `deployment` when they
  // reloaded. Summary and external_apps collapse to `summary` on refresh —
  // there's no existing DB field that cleanly distinguishes them, and the
  // summary screen is a single Continue click away from external_apps, so the
  // cost of the extra click is minimal.
  // Discord OAuth sets deployment alone, so pricing also requires the wizard-only external-apps flag.
  const [postStep, setPostStep] = useState<PostStep>(() => {
    const progress = queryClient.getQueryData<GettingStartedProgressDto | null>([
      QueryKey.gettingStarted.progress,
    ]);
    if (!progress?.external_apps_step_completed) return 'summary';
    return progress.deployment_step_completed ? 'pricing' : 'deployment';
  });
  // Domain for the resumed low-quality advisory, derived per render: same-mount
  // recovery still has the live submittedUrl (the cache may predate the run),
  // while refresh-resume has only the persisted source URL (refetched by then).
  const cachedProgress = queryClient.getQueryData<GettingStartedProgressDto | null>([
    QueryKey.gettingStarted.progress,
  ]);
  const cachedSourceUrl = cachedProgress?.automatic_source_url;
  const resumeSourceDomain = submittedUrl
    ? (extractUrlHost(submittedUrl) ?? null)
    : cachedSourceUrl
      ? (extractUrlHost(cachedSourceUrl) ?? null)
      : null;
  useEffect(() => {
    if (state !== 'completed') setPostStep('summary');
  }, [state]);

  // Not gated on `state === 'idle'`: `hasCompletedDeployment` reads this during the
  // post-step wizard, and a refresh-resume mounts straight into `completed` — so an
  // idle-only fetch would leave a connected server invisible and re-show Deployment.
  const { data: discordGuilds } = useQuery<DiscordGuildSummary[]>({
    queryKey: [QueryKey.integration.discordGuilds],
    enabled: brand === 'discord',
  });
  // Prefer OAuth confirmation time, with added_at as a rolling-deploy fallback.
  const connectedGuild = useMemo(
    () =>
      (discordGuilds ?? [])
        .filter((guild) => guild.state === 'ACTIVE' && guild.enabled)
        .reduce<DiscordGuildSummary | null>(
          (newest, guild) =>
            !newest ||
            getDiscordGuildConnectionTimestamp(guild) >= getDiscordGuildConnectionTimestamp(newest)
              ? guild
              : newest,
          null,
        ),
    [discordGuilds],
  );
  // Read once: advancing past Deployment writes this same flag, so a live read
  // would drop the step out from under the user who had just walked it — the
  // dots would lose a step and Back from Pricing would overshoot to the summary.
  const deploymentCompletedBeforeWizard = useRef(
    cachedProgress?.deployment_step_completed === true,
  ).current;
  // Discord-only: the redundant step is "connect a channel" for someone whose
  // channel is already connected. Left ungated it would also reorder the default
  // flow, where a user who reached Pricing and pressed Back would skip past
  // Deployment instead of landing on it. The guild stays live, since a server
  // can be connected in another tab while the wizard is open.
  const hasCompletedDeployment =
    brand === 'discord' && (Boolean(connectedGuild) || deploymentCompletedBeforeWizard);
  // The steps this user actually walks. Discord onboarding drops External Apps
  // (a Discord community bot is configured through the brief and Actions, not
  // by picking SaaS tools) and drops Deployment once a server is connected.
  // Navigation is by position in this list, so back/forward/Enter and the dots
  // can never disagree about what comes next.
  const postStepOrder = useMemo<PostStep[]>(
    () =>
      POST_STEP_ORDER.filter(
        (step) =>
          !(step === 'external_apps' && brand === 'discord') &&
          !(step === 'deployment' && hasCompletedDeployment),
      ),
    [brand, hasCompletedDeployment],
  );
  const stepAfter = (step: PostStep): PostStep | null =>
    postStepOrder[postStepOrder.indexOf(step) + 1] ?? null;
  const stepBefore = (step: PostStep): PostStep | null =>
    postStepOrder[postStepOrder.indexOf(step) - 1] ?? null;
  // The step list can shrink under the user — the guild query resolves after
  // mount, and a server can be connected in another tab — which would otherwise
  // leave the wizard parked on a step that no longer has a render branch. Snap
  // forward to the next step that still exists.
  useEffect(() => {
    if (state !== 'completed' || postStepOrder.includes(postStep)) return;
    const fallback =
      POST_STEP_ORDER.slice(POST_STEP_ORDER.indexOf(postStep)).find((step) =>
        postStepOrder.includes(step),
      ) ?? postStepOrder[postStepOrder.length - 1];
    setPostStep(fallback);
  }, [state, postStep, postStepOrder]);
  // Persist the post-step advance onto the progress row so a refresh can
  // resume mid-wizard. We only have a DB signal for the summary→external_apps
  // and external_apps→deployment transitions (via the existing step-completed
  // flags); no write is needed on pure backwards navigation. Uses the shared
  // mutation defaults from queryClient.ts so we just feed it a delta. On
  // success we merge only the fields we owned back into the cache — see the
  // long comment on `useGettingStartedOnboarding.mutation.onSuccess` for why
  // overwriting with the full server response can regress concurrent writes.
  const progressMutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...variables } : prev),
      );
    },
  });

  // "Start over" from a low-quality result must leave nothing behind: the URL
  // screen should be clean, not haunted by the abandoned scrape. So besides
  // resetting the flow we clear the URL draft and tear down the background site
  // map — its dock (flags + cached query) and the backend row it created.
  const discardSiteMapMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.delete],
  });
  const handleStartOver = () => {
    effectiveSetUrlDraft('');
    forgetSiteMap();
    reset();
    // Persist the reset, not just the local state: completion stamped
    // knowledge_base_step_completed, which drives refresh-resume
    // (isAutomaticResumeable), so a reload would otherwise resurrect this result.
    progressMutation.mutate({ knowledge_base_step_completed: false });
    // Pin the preview to `none` rather than removeQueries — removing makes the
    // still-enabled dock refetch and re-show the row the backend creates right
    // after "completed" — then delete the backend row (its .update() no-ops once gone).
    void queryClient.cancelQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
    queryClient.setQueryData([QueryKey.knowledgeBase.siteMapPreview.get], {
      status: 'none',
      total_urls: 0,
      host: '',
      tree: {},
    });
    discardSiteMapMutation.mutate();
  };

  // `GettingStartedFinished` (fired from the page) is the terminal conversion
  // event. The per-post-step advances we capture here sit strictly between
  // `GettingStartedAutomaticCompleted` (backend done) and `GettingStartedFinished`
  // so we can measure drop-off at each post-step instead of seeing a single
  // "automatic done" vs. "finished" gap. We only fire on forward advances —
  // going backwards is not a conversion signal.
  const advancePostStep = (next: PostStep) => {
    if (progressMutation.isPending) return;
    capture(PostHogEventEnum.GettingStartedAutomaticPostStepCompleted, {
      completed_step: postStep,
      next_step: next,
    });
    // Mirror the manual-flow semantic: persist the "completed" flag of every
    // step the user moves past, including ones this brand skips over. Skipped
    // steps must still be recorded — `postStep`'s resume derivation reads those
    // flags, so leaving them false drops the user back onto a step they never
    // needed. Slicing the canonical order (not `postStepOrder`) is what makes a
    // skip and a walk-through write the same thing.
    const delta = POST_STEP_ORDER.slice(
      POST_STEP_ORDER.indexOf(postStep),
      POST_STEP_ORDER.indexOf(next),
    ).reduce<Partial<GettingStartedProgressDto>>((acc, step) => {
      const flag = POST_STEP_FLAG[step];
      return flag ? { ...acc, [flag]: true } : acc;
    }, {});
    if (Object.keys(delta).length > 0) progressMutation.mutate(delta);
    setPostStep(next);
  };

  const advanceFrom = (from: PostStep) => {
    const next = stepAfter(from);
    if (next) advancePostStep(next);
  };
  const advanceFromSummary = () => advanceFrom('summary');

  // Terminal action for the automatic flow — flips `automatic_flow_completed` and advances to AI Preview.
  const handlePricingSkipForNow = () => {
    capture(PostHogEventEnum.GettingStartedAutomaticPostStepCompleted, {
      completed_step: 'pricing',
      next_step: 'finished',
    });
    const progress = queryClient.getQueryData<GettingStartedProgressDto | null>([
      QueryKey.gettingStarted.progress,
    ]);
    const nextSkipped: GettingStartedProgressDto['skipped_steps'] = Array.from(
      new Set([...(progress?.skipped_steps ?? []), 'pricing' as const]),
    );
    progressMutation.mutate({ skipped_steps: nextSkipped });
    onComplete?.();
  };

  const enterHandler = useMemo(() => {
    if (state !== 'completed') return null;
    // A rejected source presents a real choice: Start over is primary and
    // Continue anyway is deliberately secondary. Do not let the page-wide
    // shortcut silently choose the latter; either action must be explicit.
    if (postStep === 'pricing') return handlePricingSkipForNow;
    if (postStep === 'summary' && warningReason) return null;
    const next = stepAfter(postStep);
    return next ? () => advancePostStep(next) : null;
    // Key on `isPending` too: the advance handlers it returns read it via the
    // re-entrancy guard, so without it the closure freezes pending=true from the
    // hop that started the write and Enter stays dead after the write resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, postStep, progressMutation.isPending, warningReason, postStepOrder]);
  useEnterToAdvance(enterHandler);

  // Widen the wrapper when the 4-tier grid is showing.
  const isPricingPostStep = state === 'completed' && postStep === 'pricing';

  return (
    <div
      className={cn(
        // Horizontal padding keeps the URL input's `focus-within:ring-[3px]`
        // from getting clipped. `overflow-y-auto` implicitly collapses
        // `overflow-x: visible` to `auto` (CSS spec), which means a focus
        // ring extending past the container's left/right edge is cut — the
        // 4px padding on each side gives the 3px ring room to render fully.
        'flex flex-col gap-4 z-10 w-full max-h-[calc(100vh-140px)] px-1 overflow-y-auto custom-scroll',
        isPricingPostStep ? 'max-w-6xl' : 'max-w-2xl',
      )}
      data-testid="gs-onboarding-automatic"
    >
      {/* `mode="wait"` holds the progress card until the form has faded, which
          is the beat the submit button's spinner needs to be seen. */}
      <AnimatePresence mode="wait" initial={false}>
        {state === 'idle' && (
          <motion.div
            key="idle"
            initial={reduceMotion ? undefined : STATE_SWAP_INITIAL}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : STATE_SWAP_EXIT}
            transition={reduceMotion ? { duration: 0 } : STATE_SWAP_EXIT_TRANSITION}
          >
            {brand === 'discord' ? (
              <DiscordAutomaticIdle
                connectedGuildName={connectedGuild?.guild_name ?? null}
                setupForm={
                  <OnboardingSetupForm
                    brand="discord"
                    // Discord has no URL step, so this never fires; the form only
                    // renders the URL half for the default brand.
                    onSubmitUrl={submitUrl}
                    onSubmitDescription={(description) => submitDescription(description, 'discord')}
                    onSwitchToManual={onSkipToManual}
                    isSubmitting={isSubmitting}
                    urlDraft={effectiveUrlDraft}
                    onUrlDraftChange={effectiveSetUrlDraft}
                    descriptionDraft={effectiveDescriptionDraft}
                    onDescriptionDraftChange={effectiveSetDescriptionDraft}
                  />
                }
              />
            ) : (
              <div data-testid="gs-default-idle">
                <OnboardingSetupForm
                  brand="default"
                  onSubmitUrl={submitUrl}
                  onSubmitDescription={(description) => submitDescription(description, 'default')}
                  onSwitchToManual={onSkipToManual}
                  isSubmitting={isSubmitting}
                  urlDraft={effectiveUrlDraft}
                  onUrlDraftChange={effectiveSetUrlDraft}
                  descriptionDraft={effectiveDescriptionDraft}
                  onDescriptionDraftChange={effectiveSetDescriptionDraft}
                />
              </div>
            )}
          </motion.div>
        )}

        {state === 'processing' && (
          <motion.div
            key="processing"
            initial={reduceMotion ? undefined : STATE_SWAP_INITIAL}
            animate={{ opacity: 1, y: 0 }}
            transition={reduceMotion ? { duration: 0 } : STATE_SWAP_ENTER_TRANSITION}
          >
            <ProgressView
              steps={steps}
              submittedUrl={submittedUrl}
              warningReason={warningReason}
              sourceType={urlType}
              brand={brand}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {state === 'error' && (
        <ErrorView
          category={errorCategory ?? 'unknown'}
          urlHost={submittedUrl ? extractUrlHost(submittedUrl) : undefined}
          descriptionFlow={urlType === 'description'}
          brand={brand}
          onRetry={reset}
          onTryDifferentWebsite={() => {
            // access_blocked's retry means "different URL", so clear the futile draft first.
            effectiveSetUrlDraft('');
            reset();
          }}
          onSwitchToManual={onSwitchToManual}
        />
      )}

      {state === 'completed' && postStep === 'summary' && (
        <div className="flex flex-col gap-4">
          {steps.length === 0 ? (
            // Resume path: the scrape completed in a prior session (we
            // recovered `state === 'completed'` from the DB-backed progress
            // row) but the WS event stream that populates the agent preview
            // is gone. Render a minimal "ready" card so the user keeps moving
            // forward instead of seeing a misleading "analyzing…" header. A
            // persisted low-quality verdict restores the live summary's
            // warning treatment instead of a celebratory "ready".
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-6 min-h-[500px]">
              <div>
                <p className="text-xl font-semibold text-foreground">
                  {warningReason
                    ? t('automatic_flow.low_quality.summary_title')
                    : t('automatic_flow.phase.done')}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  {warningReason
                    ? t('automatic_flow.low_quality.summary_hint')
                    : t('automatic_flow.ready_hint')}
                </p>
              </div>
              {warningReason && (
                <Alert variant="warning" data-testid="gs-automatic-low-quality">
                  <AlertTitle>{t('automatic_flow.low_quality.title')}</AlertTitle>
                  <AlertDescription>
                    {resumeSourceDomain
                      ? t(LOW_QUALITY_COPY_KEY[warningReason], { domain: resumeSourceDomain })
                      : t('automatic_flow.low_quality.summary_hint')}
                  </AlertDescription>
                </Alert>
              )}
              {urlType === 'description' && <DescriptionSetupHint brand={brand} />}
              <div className="mt-auto flex items-center justify-end gap-3">
                <Button
                  size="sm"
                  variant={warningReason ? 'link' : undefined}
                  onClick={() => advanceFromSummary()}
                  disabled={progressMutation.isPending}
                  className={warningReason ? undefined : 'min-w-16'}
                  data-testid="gs-automatic-summary-next"
                >
                  {progressMutation.isPending && !warningReason ? (
                    <Spinner className="size-4" />
                  ) : (
                    t(warningReason ? 'automatic_flow.low_quality.continue_anyway' : 'continue')
                  )}
                </Button>
                {warningReason && (
                  <Button
                    size="sm"
                    onClick={handleStartOver}
                    className="min-w-16"
                    data-testid="gs-automatic-low-quality-start-over"
                  >
                    {t('automatic_flow.low_quality.start_over')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <ProgressView
              steps={steps}
              submittedUrl={submittedUrl}
              warningReason={warningReason}
              sourceType={urlType}
              brand={brand}
              footer={
                <>
                  {/* Low-quality source: lead with "Start over" (back to the
                      URL screen — retry, different URL, or switch to manual)
                      rather than nudging the user forward with junk context. */}
                  <Button
                    size="sm"
                    variant={warningReason ? 'link' : undefined}
                    onClick={() => advanceFromSummary()}
                    disabled={progressMutation.isPending}
                    className={warningReason ? undefined : 'min-w-16'}
                    data-testid="gs-automatic-summary-next"
                  >
                    {progressMutation.isPending && !warningReason ? (
                      <Spinner className="size-4" />
                    ) : (
                      t(warningReason ? 'automatic_flow.low_quality.continue_anyway' : 'continue')
                    )}
                  </Button>
                  {warningReason && (
                    <Button
                      size="sm"
                      onClick={handleStartOver}
                      className="min-w-16"
                      data-testid="gs-automatic-low-quality-start-over"
                    >
                      {t('automatic_flow.low_quality.start_over')}
                    </Button>
                  )}
                </>
              }
            />
          )}
          <PostStepProgressDots currentStep="summary" order={postStepOrder} />
        </div>
      )}

      {state === 'completed' && postStep === 'external_apps' && (
        <div className="flex flex-col gap-4">
          <PostStepCard
            title={t('steps.gs_step_3.title')}
            description={t('steps.gs_step_3.description')}
            onBack={() => setPostStep('summary')}
            onNext={() => advanceFrom('external_apps')}
            nextLabel={t('continue')}
            isAdvancing={progressMutation.isPending}
          >
            <ExternalAppsStep />
          </PostStepCard>
          <PostStepProgressDots currentStep="external_apps" order={postStepOrder} />
        </div>
      )}

      {state === 'completed' && postStep === 'deployment' && (
        <div className="flex flex-col gap-4">
          <PostStepCard
            title={t('steps.gs_step_4.title')}
            description={t('steps.gs_step_4.description')}
            onBack={() => setPostStep(stepBefore('deployment') ?? 'summary')}
            onNext={() => advanceFrom('deployment')}
            nextLabel={t('continue')}
            isAdvancing={progressMutation.isPending}
          >
            <DeploymentStep />
          </PostStepCard>
          <PostStepProgressDots currentStep="deployment" order={postStepOrder} />
        </div>
      )}

      {state === 'completed' && postStep === 'pricing' && (
        <div className="flex flex-col gap-4">
          <PricingStepContainer
            onBack={() => setPostStep(stepBefore('pricing') ?? 'summary')}
            onSkipForNow={handlePricingSkipForNow}
            isSkipPending={progressMutation.isPending}
          />
          <PostStepProgressDots currentStep="pricing" order={postStepOrder} />
        </div>
      )}
    </div>
  );
};
